import { motion } from "framer-motion";
import { CheckCircle2, MessageCircle, Phone } from "lucide-react";
import { WHY_US, WHY_US_REASONS, CONTACT } from "@/data/content";

const whyImage =
  "https://images.unsplash.com/photo-1781559818983-c32838ee3d55?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwyfHxsb2dpc3RpY3MlMjB3YXJlaG91c2UlMjB3b3JrZXJzfGVufDB8fHx8MTc4Mzg5NjAwMnww&ixlib=rb-4.1.0&q=85";

export default function WhyUs() {
  return (
    <section
      id="why-us"
      data-testid="why-us-section"
      className="border-b border-[#E4E7EC] bg-white"
    >
      <div className="mx-auto max-w-7xl px-5 py-20 md:px-8 md:py-28">
        {/* Header + numeric proof */}
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-12">
          <div className="lg:col-span-5">
            <p className="overline mb-4">§ 03 — Why us</p>
            <h2 className="font-display text-4xl font-black leading-[0.95] tracking-tight text-[#101828] sm:text-5xl md:text-6xl">
              Reasons forwarders <span className="text-[#D92D20]">actually switch</span> to us.
            </h2>
            <p className="mt-6 max-w-md text-base leading-relaxed text-[#475467]">
              Eight specific, measurable reasons — not marketing copy. Every
              one of these is written into our operating manual and audited
              monthly against real shipment data.
            </p>
            <div className="mt-8 overflow-hidden border-2 border-[#101828]">
              <img
                src={whyImage}
                alt="Warehouse operator handling bonded cargo — India last mile fulfilment"
                className="h-64 w-full object-cover md:h-72"
                loading="lazy"
                data-testid="why-us-image"
              />
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="grid grid-cols-2 gap-0 border-2 border-[#101828]">
              {WHY_US.map((w, i) => (
                <motion.div
                  key={w.label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className={`p-6 md:p-8 ${
                    i % 2 === 0 ? "border-r border-[#101828]" : ""
                  } ${i < WHY_US.length - 2 ? "border-b border-[#101828]" : ""}`}
                  data-testid={`stat-${i}`}
                >
                  <div className="font-display text-4xl font-black leading-none tracking-tight text-[#101828] md:text-5xl">
                    {w.stat}
                  </div>
                  <div className="mt-3 text-sm leading-snug text-[#475467]">
                    {w.label}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>

        {/* 8 conversion reasons */}
        <div className="mt-16 grid grid-cols-1 gap-0 border-2 border-[#101828] md:grid-cols-2">
          {WHY_US_REASONS.map((r, i) => (
            <motion.article
              key={r.n}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.4, delay: i * 0.04 }}
              className={`bg-white p-6 md:p-8 ${
                i % 2 === 0 ? "md:border-r md:border-[#101828]" : ""
              } ${i < WHY_US_REASONS.length - 2 ? "border-b border-[#101828]" : ""}`}
              data-testid={`why-reason-${r.n}`}
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <span className="font-mono-accent text-[#D92D20]">REASON {r.n}</span>
                  <h3 className="mt-3 font-display text-xl font-bold leading-tight tracking-tight text-[#101828] md:text-2xl">
                    {r.headline}
                  </h3>
                </div>
                <div className="text-right">
                  <div className="font-display text-3xl font-black leading-none tracking-tight text-[#D92D20]">
                    {r.metric}
                  </div>
                  <div className="font-mono-accent mt-1 text-[10px] text-[#475467]">
                    {r.metricLabel}
                  </div>
                </div>
              </div>
              <p className="mt-5 border-t border-[#E4E7EC] pt-4 text-sm leading-relaxed text-[#475467]">
                {r.proof}
              </p>
            </motion.article>
          ))}
        </div>

        {/* Closing CTA */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-40px" }}
          transition={{ duration: 0.4 }}
          className="mt-8 flex flex-col items-start justify-between gap-4 border-2 border-[#101828] bg-[#101828] p-6 text-white md:flex-row md:items-center md:p-8"
        >
          <div className="flex items-start gap-3">
            <CheckCircle2 className="mt-0.5 h-6 w-6 flex-none text-[#25D366]" strokeWidth={2.5} />
            <div>
              <p className="font-display text-lg font-bold leading-tight tracking-tight md:text-xl">
                Try us on a single HAWB. Zero commitment.
              </p>
              <p className="mt-1 text-sm text-[#98A2B3]">
                One shipment tells you more than a 60-minute sales call. WhatsApp us the pre-alert.
              </p>
            </div>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row">
            <a
              href={CONTACT.whatsappHref}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="whyus-whatsapp-cta"
              className="inline-flex items-center justify-center gap-2 border-2 border-[#25D366] bg-[#25D366] px-5 py-3 text-sm font-semibold text-[#0B111A] hover:bg-[#1FBE5A]"
            >
              <MessageCircle className="h-4 w-4" strokeWidth={2.5} /> WhatsApp the desk
            </a>
            <a
              href={CONTACT.telHref}
              data-testid="whyus-call-cta"
              className="inline-flex items-center justify-center gap-2 border-2 border-white bg-transparent px-5 py-3 text-sm font-semibold text-white hover:bg-white hover:text-[#101828]"
            >
              <Phone className="h-4 w-4" strokeWidth={2.5} /> {CONTACT.phoneDisplay}
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
